import React from 'react'
import styled, { css, keyframes } from 'styled-components'
import { string, number } from 'prop-types'
import { rgba } from 'polished'

import { white } from 'styles/colors'
import { fontWeights, fontSizes } from 'styles/typography'
import { media, mediaDown } from 'styles/media'
import { square, animationTime, animationCurve } from 'styles/global'

const pulse = keyframes`
  0% { transform: scale(0.1); opacity: 0; }
  50% { opacity: 1; }
  100% { transform: scale(1.2); opacity: 0; }
`

const Container = styled.div`
  position: absolute;
  z-index: 10;
  transform: translate3d(-50%, -50%, 0);
`

const Dot = styled.div`
  position: relative;
  background-color: ${white};
  border-radius: 50%;
  cursor: pointer;
  ${square('7px')};

  &:focus {
    outline: 0;
  }

  ${media.medium`
    ${square('10px')};
  `};

  &:after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    margin-left: -9px;
    margin-top: -9px;
    background-color: ${rgba(white, 0.35)};
    border-radius: 50%;
    opacity: 0;
    transform-origin: center center;
    animation: ${pulse} 2400ms ease-out infinite;
    ${square`18px`};

    ${media.medium`
      margin-left: -14px;
      margin-top: -14px;
      ${square`28px`};
    `};
  }

  &:before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate3d(-50%, -50%, 0);
    ${square('30px')}; // Increase touch area
  }
`

const Text = styled.span`
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  color: ${white};
  font-weight: ${fontWeights.medium};
  width: 85px;
  opacity: 0;
  visibility: hidden;
  transition: opacity ${animationTime} ${animationCurve}, visibility ${animationTime} ${animationCurve};
  font-size: 9px;

  ${Dot}:hover + &,
  ${Dot}:focus + & {
    opacity: 1;
    visibility: visible;
  }

  ${media.small`
    width: 140px;
    font-size: ${fontSizes.tiny};
  `};

  ${media.medium`
    margin-left: ${props => props.offset}px;
    
    ${props =>
      props.direction === 'left' &&
      css`
        left: auto;
        right: 0;
        text-align: right;
        margin-left: 0;
        margin-right: ${props => props.offset}px;
      `}
  `};

  ${mediaDown.medium`
    
    ${props =>
      props.mobileDirection === 'left' &&
      css`
        left: auto;
        right: 0;
        text-align: right;
        margin-left: 0;
        margin-right: ${props => props.mobileOffset}px;
      `}

      ${props =>
        props.mobileDirection === 'right' &&
        css`
          right: auto;
          left: 0;
          text-align: left;
          margin-right: 0;
          margin-left: ${props => props.mobileOffset}px;
        `}
  `};

  ${media.medium`
    width: 160px;
    font-size: ${fontSizes.small};
  `};

  ${media.large`
    width: 190px;
    font-size: ${fontSizes.regular};
  `};

  ${media.xLarge`
    width: 220px;
  `};
`

const Marker = ({ x, y, text, direction, mobileDirection, offset, mobileOffset }) => (
  <Container
    css={`
      top: ${y};
      left: ${x};
    `}
  >
    <Dot tabIndex="0" role="button" />
    <Text offset={offset} mobileOffset={mobileOffset} direction={direction} mobileDirection={mobileDirection}>
      {text}
    </Text>
  </Container>
)

Marker.propTypes = {
  x: string.isRequired,
  y: string.isRequired,
  text: string.isRequired,
  direction: string,
  mobileDirection: string,
  offset: number,
  mobileOffset: number
}

export default Marker
